import { FunctionComponent, useEffect, useState } from 'react';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import Button from 'components/UI/V3/Button/Button';
import styles from './SetupCommissionsDrawer.module.scss';
import { useApiClientWrapper } from 'hooks';
import { dispatch } from 'store';
import { saveMarketplaceThunk } from '@spike/marketplace-action';
import Marketplace from '@spike/marketplace-model';
import { setTimeZone } from 'actions/login/LoginActions';
import { Radio } from '../V3/Forms/Radio';

export interface SetupCommissionsDrawerProps {
    marketplace: Marketplace;
    onClose?: () => void;
}

type CommissionCalculationOption = 'before_discount' | 'after_discount';

const COMMISSION_CALCULATION_SETTING_KEY = 'commission_calculation_basis';

const OPTIONS: CommissionCalculationOption[] = ['before_discount', 'after_discount'];

const LABELS: Record<CommissionCalculationOption, string> = {
    before_discount: 'Before Discounts / Promotions',
    after_discount: 'After Discounts / Promotions'
};

const DESCRIPTIONS: Record<CommissionCalculationOption, string> = {
    before_discount: 'Commissions are calculated using the original service price, prior to discounts and promotions.',
    after_discount: 'Commissions are calculated on the service price after discounts and promotions are applied.'
};

export const SetupCommissionsDrawer: FunctionComponent<SetupCommissionsDrawerProps> = ({ marketplace, onClose }) => {
    const apiClientWrapper = useApiClientWrapper();
    const [selectedValue, setSelectedValue] = useState<CommissionCalculationOption>('before_discount');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const currentSetting = marketplace.settings?.find(
            setting => setting.key === COMMISSION_CALCULATION_SETTING_KEY
        );

        setSelectedValue((currentSetting?.value as CommissionCalculationOption) || 'before_discount');
    }, [marketplace]);

    const handleSave = async () => {
        const newMarketplace = {
            ...marketplace,
            settings: marketplace.settings?.map(setting => {
                if (setting.key === COMMISSION_CALCULATION_SETTING_KEY) {
                    return {
                        ...setting,
                        value: selectedValue
                    };
                }

                return setting;
            })
        };

        try {
            setIsSaving(true);
            await dispatch(saveMarketplaceThunk(apiClientWrapper, newMarketplace, setTimeZone));
            onClose?.();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader
                title="Setup Commissions Calculation"
                onClose={onClose}
            />

            <DrawerBody className={styles.body}>
                <p className={styles.subtitle}>Choose how to calculate staff commissions.</p>

                <div className={styles.optionsList}>
                    {OPTIONS.map(option => {
                        const isSelected = selectedValue === option;

                        return (
                            <button
                                key={option}
                                type="button"
                                className={isSelected ? styles.optionCardSelected : styles.optionCard}
                                onClick={() => setSelectedValue(option)}
                            >
                                <div className={styles.optionRadio}>
                                    <Radio
                                        checked={isSelected}
                                        onChange={() => setSelectedValue(option)}
                                    />
                                </div>

                                <div className={styles.optionBody}>
                                    <div className={styles.optionTitle}>{LABELS[option]}</div>
                                    <div className={styles.optionDescription}>{DESCRIPTIONS[option]}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </DrawerBody>

            <DrawerFooter>
                <Button
                    label="Save"
                    size="large"
                    variant="green"
                    isLoading={isSaving}
                    onClick={handleSave}
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default SetupCommissionsDrawer;
